import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ArrowUpRight,
  ArrowDown,
  MapPin,
  Clock,
  Briefcase,
  Globe,
  HeartPulse,
  BookOpen,
  Coins,
  Rocket,
  Sun,
  Mail,
} from 'lucide-react';
import SEO from '@/components/SEO';
import { site } from '@/data/content';

const benefits = [
  {
    icon: 'Globe',
    title: 'Remote-first',
    text: 'Work from anywhere with a team spread across time zones. We care about output, not office hours.',
  },
  {
    icon: 'HeartPulse',
    title: 'Health & wellness',
    text: 'Comprehensive health cover and a wellness budget so you can do your best work.',
  },
  {
    icon: 'BookOpen',
    title: 'Learning budget',
    text: 'Annual budget for courses, conferences, books, and anything that levels up your craft.',
  },
  {
    icon: 'Coins',
    title: 'Meaningful equity',
    text: 'Ownership shared with every full-time team member, because your work deserves a stake.',
  },
  {
    icon: 'Rocket',
    title: 'Ship fast',
    text: 'Small team, high ownership. You will see your code, design, or copy in production quickly.',
  },
  {
    icon: 'Sun',
    title: 'Work-life balance',
    text: 'Flexible hours, generous time off, and the freedom to work when you are at your best.',
  },
];

const roles = [
  { title: 'Senior Frontend Engineer', dept: 'Engineering', loc: 'Remote', type: 'Full-time' },
  { title: 'Product Designer', dept: 'Design', loc: 'Remote', type: 'Full-time' },
  { title: 'Customer Success Manager', dept: 'Success', loc: 'Remote', type: 'Full-time' },
  { title: 'Growth Marketer', dept: 'Marketing', loc: 'Remote', type: 'Full-time' },
];

const iconMap: Record<string, typeof Globe> = {
  Globe,
  HeartPulse,
  BookOpen,
  Coins,
  Rocket,
  Sun,
};

export default function Careers() {
  return (
    <>
      <SEO
        title="Careers"
        description={`Join ${site.name} and help build the future of customer experience.`}
        path="/careers"
      />

      <section className="relative overflow-hidden bg-slate-950 px-4 pt-28 pb-20 text-white sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <h1 className="text-4xl font-extrabold tracking-tight sm:text-6xl">
            Build the future of <span className="gradient-text">customer experience</span>
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg text-slate-300">
            Remote-first, high ownership, small team. We move fast, ship daily, and obsess over the teams we serve.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a
              href="#open-roles"
              className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3 text-base font-bold text-slate-900 transition hover:bg-slate-100"
            >
              View roles <ArrowDown className="h-4 w-4" />
            </a>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-7 py-3 text-base font-semibold text-white transition hover:bg-slate-800"
            >
              Our story <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-20" aria-labelledby="benefits-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="benefits-heading" className="section-title">Why work here</h2>
            <p className="section-subtitle mx-auto">Perks that match the way we work.</p>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {benefits.map((b) => {
              const Icon = iconMap[b.icon];
              return (
                <div
                  key={b.title}
                  className="flex flex-col items-start gap-4 rounded-2xl border border-slate-100 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-700">
                    <Icon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">{b.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{b.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20" aria-labelledby="culture-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <h2 id="culture-heading" className="text-3xl font-extrabold text-slate-900 sm:text-4xl">
                How we work
              </h2>
              <p className="mt-4 text-lg text-slate-600">
                We are a small, ambitious team solving a big problem. You will work directly with founders, take real ownership, and see your impact in the market.
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  'Async by default, meetings by exception.',
                  'Two-week shipping cycles with clear outcomes.',
                  'Customer feedback in every roadmap conversation.',
                  'No politics, no endless processes.',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-slate-700">
                    <Rocket className="mt-0.5 h-5 w-5 shrink-0 text-brand-600" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-3xl border border-slate-100 bg-slate-50 p-8">
              <p className="text-2xl font-bold text-slate-900">“We are building the tool we wish we had when we were scaling our own business.”</p>
              <p className="mt-4 text-slate-600">Bahati Asher Faith, Co-founder</p>
            </div>
          </div>
        </div>
      </section>

      <section id="open-roles" className="bg-slate-50 py-20" aria-labelledby="roles-heading">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="roles-heading" className="section-title">Open roles</h2>
            <p className="section-subtitle mx-auto">Apply by email or start a workspace to learn the product.</p>
          </div>
          <div className="mt-10 space-y-3">
            {roles.map((role) => (
              <div
                key={role.title}
                className="flex flex-col items-start justify-between gap-4 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm transition hover:-translate-y-1 hover:shadow-lg sm:flex-row sm:items-center"
              >
                <div>
                  <h3 className="text-lg font-bold text-slate-900">{role.title}</h3>
                  <div className="mt-1 flex flex-wrap gap-3 text-sm text-slate-500">
                    <span className="inline-flex items-center gap-1 font-medium text-brand-600">
                      <Briefcase className="h-4 w-4" /> {role.dept}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <MapPin className="h-4 w-4" /> {role.loc}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Clock className="h-4 w-4" /> {role.type}
                    </span>
                  </div>
                </div>
                <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
                  <a
                    href={`mailto:bahati@zurichatmails.com?subject=Application%20for%20${encodeURIComponent(role.title)}&body=Hi%20team,%0D%0A%0D%0AI%20am%20interested%20in%20the%20${encodeURIComponent(role.title)}%20role.%20Please%20find%20my%20details%20below.%0D%0A%0D%0AThank%20you.`}
                    className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-slate-800 sm:flex-none"
                  >
                    Apply <Mail className="h-4 w-4" />
                  </a>
                  <a
                    href={site.workspaceUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-bold text-slate-700 transition hover:border-brand-300 hover:text-brand-700 sm:flex-none"
                  >
                    Try workspace <ArrowUpRight className="h-4 w-4" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-900 py-20 text-white" aria-label="Apply now">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">No perfect role?</h2>
          <p className="mx-auto mt-3 max-w-2xl text-lg text-slate-300">
            We are always open to smart, kind people. Send your best work and we will be in touch.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a
              href="mailto:bahati@zurichatmails.com?subject=General%20application"
              className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3 text-base font-bold text-slate-900 transition hover:bg-slate-100"
            >
              Send your resume <Mail className="h-4 w-4" />
            </a>
            <a
              href={site.workspaceUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-7 py-3 text-base font-semibold text-white transition hover:bg-slate-800"
            >
              Explore workspace <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
